import type { RawPointerEventPayload } from '../../viewer/moudles/EventManager';
import type { EntitySelectionManager } from '../EntitySelectionManager';
import { isSketchLineObject } from '../../mesh/sketch';
import { SketchEntitySelectionHandler } from './SketchEntitySelectionHandler';
import type * as THREE from 'three';

/**
 * 裁剪模式专用选择处理类。
 * 职责：
 * 1. 继承草图默认处理器基础能力；
 * 2. 裁剪态下保留 hover 命中与高亮；
 * 3. 禁止默认框选与拖拽；
 * 4. 提供裁剪工作流需要的草图曲线点击切换选择。
 */
export class SketchTrimEntitySelectionHandler extends SketchEntitySelectionHandler {
    /** 当前裁剪子模式允许选择的实体判定器。 */
    private selectableEntityPredicate: (object: THREE.Object3D) => boolean = isSketchLineObject;

    /**
     * 构造函数。
     * @param entitySelectionManager 实体选择管理器。
     */
    public constructor(entitySelectionManager: EntitySelectionManager) {
        super(entitySelectionManager);
    }

    /**
     * 配置当前裁剪子模式允许选择的实体。
     *
     * 说明：Corner、Inside/Outside 会按各自选择阶段注入开放曲线或边界曲线规则；
     * 选择能力由命令控制器注入，避免选择处理器反向依赖具体裁剪业务模式。
     *
     * @param predicate 实体准入判定器。
     */
    public setSelectableEntityPredicate(predicate: (object: THREE.Object3D) => boolean): void {
        this.selectableEntityPredicate = predicate;
    }

    /**
     * 收集当前裁剪子模式可点击的实体。
     *
     * @returns 已按领域准入规则过滤的候选。
     */
    protected override collectClickSelectionTargets(): THREE.Object3D[] {
        return super.collectClickSelectionTargets().filter(this.selectableEntityPredicate);
    }

    /**
     * 收集当前裁剪子模式可悬停的实体。
     *
     * 点击和悬停必须使用同一准入规则，避免闭合曲线显示可选高亮但点击无响应。
     *
     * @returns 已按领域准入规则过滤的候选。
     */
    protected override collectHoverSelectionTargets(): THREE.Object3D[] {
        return super.collectHoverSelectionTargets().filter(this.selectableEntityPredicate);
    }

    /**
     * 保留 Trim 命令实际命中的组成曲线。
     *
     * 普通草图选择会把槽口的直线或圆弧提升为槽口整体，便于整体编辑；Trim 的语义
     * 则是修改命中的单个草图片段，因此必须保留原始子曲线，避免整体对象被准入规则拒绝。
     *
     * @param picked 原始命中结果。
     * @returns 未做复合对象提升的原始命中结果。
     */
    protected override resolveClickSelectionPick(
        picked: THREE.Intersection<THREE.Object3D> | null,
    ): THREE.Intersection<THREE.Object3D> | null {
        return picked;
    }

    /**
     * 处理鼠标按下。
     * @param payload 原始事件。
     */
    public override onMouseDown(payload: RawPointerEventPayload): void {
        void payload;
    }

    /**
     * 处理鼠标移动。
     * @param payload 原始事件。
     */
    public override onMouseMove(payload: RawPointerEventPayload): void {
        const hoveredPick = this.pickEntityAt(
            payload.clientX,
            payload.clientY,
            this.collectHoverSelectionTargets(),
        );
        this.updateHoverState(this.resolveHoverPickWithSelectionExclusion(hoveredPick));
    }

    /**
     * 处理鼠标抬起。
     * @param payload 原始事件。
     */
    public override onMouseUp(payload: RawPointerEventPayload): void {
        void payload;
    }

    /**
     * 处理鼠标点击。
     * @param payload 原始事件。
     */
    public override onClick(payload: RawPointerEventPayload): void {
        if (payload.button !== 0) {
            return;
        }

        const picked = this.resolveClickSelectionPick(this.pickEntityAt(
            payload.clientX,
            payload.clientY,
            this.collectClickSelectionTargets(),
        ));

        if (!picked) {
            return;
        }

        if (!this.selectableEntityPredicate(picked.object)) {
            return;
        }

        const currentSelectedPicks = this.entitySelectionManager.getSelectedPicks()
            .filter((item): item is typeof picked => this.selectableEntityPredicate(item.object));
        const isAlreadySelected = currentSelectedPicks.some((item) => item.object.uuid === picked.object.uuid);
        const nextSelectedPicks = isAlreadySelected
            ? currentSelectedPicks.filter((item) => item.object.uuid !== picked.object.uuid)
            : [...currentSelectedPicks.filter((item) => item.object.uuid !== picked.object.uuid), picked];

        this.entitySelectionManager.updateSelectionState(nextSelectedPicks);
    }
}
